import { useRef, useState } from "react";
import api, { apiErr, fmtIDR } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fl } from "@/lib/finI18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileDown, Upload, Loader2, AlertTriangle } from "lucide-react";

function Preview({ res, lang }) {
  return (
    <div className="max-h-[45vh] overflow-auto rounded-md border border-border">
      <Table>
        <TableHeader><TableRow><TableHead className="w-20">{fl("code", lang)}</TableHead><TableHead>{fl("account", lang)}</TableHead><TableHead>{fl("ledger", lang)}</TableHead>
          <TableHead className="text-right">{fl("debit", lang)}</TableHead><TableHead className="text-right">{fl("credit", lang)}</TableHead></TableRow></TableHeader>
        <TableBody>
          {res.items.map((x, i) => (
            <TableRow key={`${x.coa_id}-${x.ledger}`} data-testid={`opening-import-row-${i}`}>
              <TableCell className="num text-xs">{x.code}</TableCell>
              <TableCell className="text-xs">{x.name}</TableCell>
              <TableCell className="text-xs">{x.ledger || "—"}</TableCell>
              {x.amount_original != null
                ? <TableCell colSpan={2} className="num text-right text-xs">{x.currency} {Number(x.amount_original).toLocaleString("id-ID", { maximumFractionDigits: 2 })}
                  {x.idr != null && <span className="text-muted-foreground"> → {fmtIDR(x.idr)} @ {Number(x.fx_rate).toLocaleString("id-ID", { maximumFractionDigits: 4 })} · KMK {x.kmk_no}</span>}</TableCell>
                : <><TableCell className="num text-right text-xs">{x.debit ? fmtIDR(x.debit) : ""}</TableCell><TableCell className="num text-right text-xs">{x.credit ? fmtIDR(x.credit) : ""}</TableCell></>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function OpeningImport({ clientId, year, start, onDone }) {
  const { lang } = useI18n();
  const ref = useRef(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const template = async () => {
    try {
      const r = await api.get(`/opening-balances/template?client_id=${clientId}&year=${year}`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `template_saldo_awal_${year}.xlsx`; a.click();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const fd = new FormData(); fd.append("client_id", clientId); fd.append("year", year); fd.append("start_date", start); fd.append("file", f);
    setBusy(true);
    try { setRes((await api.post("/opening-balances/parse", fd)).data); } catch (er) { toast.error(apiErr(er.response?.data?.detail)); }
    setBusy(false);
  };
  const apply = async () => {
    const items = res.items.map(({ coa_id, ledger, debit, credit, amount_original }) => ({ coa_id, ledger, debit, credit, ...(amount_original != null ? { amount_original } : {}) }));
    setBusy(true);
    try { await api.post("/opening-balances", { client_id: clientId, year, items, start_date: start }); toast.success(fl("obImportDone", lang)); setRes(null); onDone(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  const balanced = res && Math.round(res.total_debit - res.total_credit) === 0;
  return (
    <>
      <Button variant="outline" onClick={template} data-testid="opening-template"><FileDown className="h-4 w-4 mr-1" />{fl("obTemplate", lang)}</Button>
      <Button variant="outline" onClick={() => ref.current?.click()} disabled={busy || !clientId} data-testid="opening-import">
        {busy && !res ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}{fl("obImport", lang)}</Button>
      <input ref={ref} type="file" accept=".xlsx,.csv" className="hidden" onChange={pick} data-testid="opening-import-file" />
      <Dialog open={!!res} onOpenChange={(o) => { if (!o) setRes(null); }}>
        <DialogContent className="max-w-3xl" data-testid="opening-import-dialog">
          <DialogHeader><DialogTitle>{fl("obImportTitle", lang).replace("{y}", year)}</DialogTitle></DialogHeader>
          {res && (
            <div className="space-y-3 text-sm">
              <p className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800" data-testid="opening-import-warn"><AlertTriangle className="h-4 w-4 shrink-0" />{fl("obImportWarn", lang).replace("{y}", year)}</p>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="outline" data-testid="opening-import-count">{fl("obImportRows", lang).replace("{n}", res.items.length)}</Badge>
                {res.fx_count > 0 && <Badge variant="outline" data-testid="opening-import-fx">{fl("obImportFx", lang).replace("{n}", res.fx_count)}</Badge>}
                <span className={`num ${balanced ? "text-emerald-600" : "text-rose-600"}`} data-testid="opening-import-total">
                  {fl("debit", lang)} {fmtIDR(res.total_debit)} · {fl("credit", lang)} {fmtIDR(res.total_credit)} — {fl(balanced ? "obBalanced" : "obUnbalanced", lang)}
                </span>
              </div>
              {res.errors.length > 0 && (
                <div className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700 max-h-28 overflow-auto" data-testid="opening-import-errors">
                  <p className="font-semibold mb-1">{fl("obImportErrors", lang)} ({res.errors.length})</p>
                  {res.errors.map((er) => <p key={er}>{er}</p>)}
                </div>
              )}
              <Preview res={res} lang={lang} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRes(null)} data-testid="opening-import-cancel">{fl("cancel", lang)}</Button>
            <Button onClick={apply} disabled={busy || !res?.items.length} data-testid="opening-import-confirm">
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}{fl("obImportApply", lang)}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
